import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Loader2, TrendingUp, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/**
 * Landing page for invite-by-email and password-recovery flows.
 *
 * Supabase Auth (with `detectSessionInUrl: true`) automatically reads
 * `?code=…` (PKCE) or `#access_token=…&refresh_token=…&type=invite|recovery`
 * from the URL on mount and creates a session. We just have to (a) check
 * a session exists, (b) prompt for a new password, and (c) write it via
 * `supabase.auth.updateUser({ password })`.
 *
 * Without this page the invite link landed on /auth which only renders a
 * sign-in form — invited users had no place to set their password.
 */

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(72, "Password must be at most 72 characters");

type LoadState = "initialising" | "ready" | "no-session" | "submitting" | "done";

const SetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("initialising");

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      // detectSessionInUrl runs ahead of us, so by the time React mounts
      // the session should already be in place. Wait a tick for the
      // client to settle, then read it.
      await new Promise((r) => setTimeout(r, 50));
      const { data, error } = await supabase.auth.getSession();
      if (cancelled) return;
      if (error || !data.session?.user) {
        setLoadState("no-session");
        return;
      }
      setLoadState("ready");
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.errors[0]?.message ?? "Invalid password");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoadState("submitting");
    const { error: updateError } = await supabase.auth.updateUser({
      password,
    });
    if (updateError) {
      setLoadState("ready");
      setError(updateError.message);
      return;
    }
    setLoadState("done");
    toast({
      title: "Password set",
      description: "You can now sign in with your email and password.",
    });
    // Brief pause so the success state is visible.
    setTimeout(() => navigate("/dashboard", { replace: true }), 800);
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-accent-foreground" />
          </div>
          <span className="text-xl font-semibold text-white">EquityStart</span>
        </div>
        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight">
            Welcome to<br />EquityStart
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            Set a password to finish setting up your account. You'll use it to
            sign in next time.
          </p>
        </div>
        <p className="text-sm text-white/60">© 2024 EquityStart. Internal use only.</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-bold">Set your password</h2>
            <p className="text-muted-foreground mt-2">
              Choose a strong password to finish setting up your account.
            </p>
          </div>

          {loadState === "initialising" && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {loadState === "no-session" && (
            <div className="space-y-4 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm">
              <div className="flex items-start gap-2 text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <div>
                  <p className="font-medium">Invite link expired or invalid</p>
                  <p className="text-muted-foreground">
                    Ask an admin to re-invite you. The link in the email
                    expires after a short period.
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={() => navigate("/auth")}>
                Back to sign in
              </Button>
            </div>
          )}

          {loadState === "done" && (
            <div className="space-y-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
              <div className="flex items-start gap-2 text-emerald-700">
                <CheckCircle className="w-4 h-4 mt-0.5" />
                <p className="font-medium">Password set. Redirecting…</p>
              </div>
            </div>
          )}

          {(loadState === "ready" || loadState === "submitting") && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
              <Button
                type="submit"
                className="w-full h-11"
                disabled={loadState === "submitting"}
              >
                {loadState === "submitting" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Set password and continue"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
